import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Eye, History, Loader2, Save } from 'lucide-react';
import { Heading } from '../../layout';
import { useUIColors } from '../../../hooks/useTheme';
import platformSettingsAPI from '../../../services/platformSettingsAPI';
import AdministrationBackLink from '../AdministrationBackLink';
import MaintenanceNoticeModal from '../MaintenanceNoticeModal';

const EMPTY_NOTICE = {
  enabled: false,
  title: '',
  message: '',
  schedule: '',
};

const formatUpdatedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' });
};

const MaintenanceNoticeWorkspace = ({ isDarkMode = false, showAdminBack = false }) => {
  const ui = useUIColors();

  const [form, setForm] = useState(EMPTY_NOTICE);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: '' });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const applyNotice = (data) => {
    setForm({
      enabled: Boolean(data?.enabled),
      title: data?.title || '',
      message: data?.message || '',
      schedule: data?.schedule || '',
    });
    setMeta({ updated_at: data?.updated_at || null, updated_by: data?.updated_by || '' });
    setHistory(Array.isArray(data?.history) ? data.history : []);
  };

  const loadNotice = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      applyNotice(await platformSettingsAPI.getMaintenanceNotice());
    } catch (err) {
      setError(err?.message || 'No se pudo cargar el aviso de mantención.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotice();
  }, [loadNotice]);

  const handleChange = (key, value) => {
    setInfo('');
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUseHistoryEntry = (entry) => {
    setInfo('');
    setError('');
    setForm((prev) => ({
      ...prev,
      title: entry.title || '',
      message: entry.message || '',
      schedule: entry.schedule || '',
    }));
  };

  const isCurrentText = (entry) =>
    entry.title === form.title.trim() &&
    entry.message === form.message.trim() &&
    (entry.schedule || '') === String(form.schedule || '').trim();

  const handleSave = async () => {
    if (!String(form.title || '').trim() || !String(form.message || '').trim()) {
      setError('El título y el mensaje son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const saved = await platformSettingsAPI.updateMaintenanceNotice({
        enabled: form.enabled,
        title: form.title.trim(),
        message: form.message.trim(),
        schedule: String(form.schedule || '').trim(),
      });
      applyNotice(saved);
      setInfo(
        saved?.enabled
          ? 'Aviso guardado. Los usuarios conectados lo verán en menos de un minuto y el resto al iniciar sesión.'
          : 'Aviso desactivado.'
      );
    } catch (err) {
      setError(err?.message || 'No se pudo guardar el aviso de mantención.');
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: 12,
    background: ui.panel,
    padding: '16px 18px',
    display: 'grid',
    gap: 14,
    maxWidth: 720,
  };

  const labelStyle = { fontSize: 12, fontWeight: 650, color: ui.text, display: 'grid', gap: 6 };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.inputText,
    fontSize: 13,
    padding: '8px 10px',
    fontFamily: 'inherit',
  };

  const secondaryButtonStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: ui.header,
    color: ui.text,
    fontWeight: 700,
    fontSize: 13,
    padding: '0 14px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  };

  const updatedAtLabel = formatUpdatedAt(meta.updated_at);
  const disabled = loading || saving;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Mantención
        </Heading>
        {showAdminBack ? <AdministrationBackLink /> : null}
        <p style={{ margin: '4px 0 0', fontSize: 13, color: ui.muted, maxWidth: 640, lineHeight: 1.5 }}>
          Muestra una ventana emergente a todos los usuarios: a los que ya están conectados durante su
          sesión, y al resto justo después de iniciar sesión. El aviso se puede cerrar y la plataforma
          sigue funcionando con normalidad.
        </p>
      </div>

      <div style={cardStyle}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '10px 12px',
            borderRadius: 10,
            border: `1px solid ${form.enabled ? ui.warning : ui.border}`,
            background: form.enabled ? `color-mix(in srgb, ${ui.warning} 10%, ${ui.panel})` : ui.header,
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          <span>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 750, color: ui.text }}>
              {form.enabled ? 'Aviso activo' : 'Aviso inactivo'}
            </span>
            <span style={{ display: 'block', marginTop: 2, fontSize: 12, color: ui.muted }}>
              {form.enabled
                ? 'Se mostrará a los usuarios conectados y a quienes inicien sesión.'
                : 'Actívalo cuando la plataforma esté en mantención.'}
            </span>
          </span>
          <input
            type="checkbox"
            checked={form.enabled}
            disabled={disabled}
            onChange={(event) => handleChange('enabled', event.target.checked)}
            style={{ width: 18, height: 18, accentColor: ui.warning, cursor: 'inherit' }}
          />
        </label>

        <label style={labelStyle}>
          Título
          <input
            style={inputStyle}
            value={form.title}
            maxLength={120}
            disabled={disabled}
            onChange={(event) => handleChange('title', event.target.value)}
            placeholder="Plataforma en mantención"
          />
        </label>

        <label style={labelStyle}>
          Mensaje
          <textarea
            style={{ ...inputStyle, minHeight: 110, resize: 'vertical', lineHeight: 1.5 }}
            value={form.message}
            maxLength={2000}
            disabled={disabled}
            onChange={(event) => handleChange('message', event.target.value)}
            placeholder="Describe qué está pasando y qué pueden esperar los usuarios."
          />
        </label>

        <label style={labelStyle}>
          Horario (opcional)
          <input
            style={inputStyle}
            value={form.schedule}
            maxLength={200}
            disabled={disabled}
            onChange={(event) => handleChange('schedule', event.target.value)}
            placeholder="Ej: Sábado 27/09 de 09:00 a 13:00"
          />
        </label>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled}
            style={{
              ...secondaryButtonStyle,
              border: 'none',
              background: ui.interactive,
              color: '#fff',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.65 : 1,
            }}
          >
            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            disabled={loading || !String(form.title || '').trim()}
            style={secondaryButtonStyle}
          >
            <Eye size={16} />
            Vista previa
          </button>
          {updatedAtLabel ? (
            <span style={{ fontSize: 12, color: ui.muted }}>
              Última actualización: {updatedAtLabel}
              {meta.updated_by ? ` · ${meta.updated_by}` : ''}
            </span>
          ) : null}
        </div>

        {error ? <p style={{ margin: 0, fontSize: 13, color: ui.danger }}>{error}</p> : null}
        {info ? <p style={{ margin: 0, fontSize: 13, color: ui.success }}>{info}</p> : null}
      </div>

      {history.length ? (
        <div style={{ ...cardStyle, gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <History size={16} color={ui.interactive} />
            <span style={{ fontSize: 14, fontWeight: 750, color: ui.text }}>Avisos anteriores</span>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
            Se guardan los últimos avisos activados. Usa uno para cargar su texto en el formulario y
            luego guarda.
          </p>
          {history.map((entry) => {
            const current = isCurrentText(entry);
            const usedAt = formatUpdatedAt(entry.used_at);
            return (
              <div
                key={`${entry.used_at}-${entry.title}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${current ? ui.interactive : ui.border}`,
                  background: ui.header,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: ui.text }}>{entry.title}</div>
                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 12,
                      color: ui.textSecondary,
                      lineHeight: 1.45,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {entry.message}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 10,
                      fontSize: 11,
                      color: ui.muted,
                    }}
                  >
                    {entry.schedule ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <CalendarClock size={12} />
                        {entry.schedule}
                      </span>
                    ) : null}
                    {usedAt ? <span>Activado: {usedAt}</span> : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleUseHistoryEntry(entry)}
                  disabled={disabled || current}
                  style={{
                    ...secondaryButtonStyle,
                    height: 30,
                    fontSize: 12,
                    padding: '0 12px',
                    flexShrink: 0,
                    cursor: disabled || current ? 'default' : 'pointer',
                    opacity: current ? 0.6 : 1,
                  }}
                >
                  {current ? 'En uso' : 'Usar'}
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      <MaintenanceNoticeModal
        isOpen={showPreview}
        notice={form}
        onClose={() => setShowPreview(false)}
      />
    </div>
  );
};

export default MaintenanceNoticeWorkspace;
